/* eslint-disable react/no-danger */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-confusing-void-expression */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { Link, Element } from 'react-scroll';
import {
  Box, Typography, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';

interface Abstraction {
  id: number;
  title: string;
  description: string;
  page_number: number;
  pdf_url: string;
  document: number | null;
}

interface DocumentAbstractions {
  document: {
    id: number;
    document_type: string;
    original_filename: string;
    effective_date: string | null;
    ordinal: number;
    is_processed: boolean;
    progress: number;
    message: string;
    pdf_url: string;
    download_url: string;
  };
  abstractions: Abstraction[];
  headings: string[];
}

interface AmendmentAbstractionResponse {
  documents: DocumentAbstractions[];
}

interface AmendmentAbstractionTabProps {
  leaseId: number;
}

function normalizeHtmlContent(htmlContent: string): string {
  const container = document.createElement('div');
  container.innerHTML = htmlContent;
  const boldTags = container.querySelectorAll('b');
  boldTags.forEach((boldTag) => {
    const { nextSibling } = boldTag;
    if (nextSibling && nextSibling.nodeName === 'B') {
      const br = document.createElement('br');
      boldTag.parentNode?.insertBefore(br, nextSibling);
    }
  });
  return container.innerHTML;
}

export default function AmendmentAbstractionTab({ leaseId }: AmendmentAbstractionTabProps): JSX.Element {
  const [expandedDoc, setExpandedDoc] = useState<number | false>(0);

  const { data, isLoading, error } = useQuery(
    ['amendment-abstractions', leaseId],
    async () => {
      const res = await axios.get<AmendmentAbstractionResponse>(
        `api/leases/${leaseId}/amendment_abstractions/`,
      );
      return res.data;
    },
    { enabled: !!leaseId },
  );

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress sx={{ color: '#00CFA1' }} size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign='center' py={4}>
        <Typography color='error'>Failed to load amendment abstractions</Typography>
      </Box>
    );
  }

  if (!data || data.documents.length === 0) {
    return (
      <Box textAlign='center' py={4}>
        <Typography color='textSecondary' sx={{ fontSize: '14px' }}>
          No amendments have been uploaded for this lease.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: '16px 24px' }}>
      {data.documents.map((docGroup, idx) => {
        const { document: doc, abstractions, headings } = docGroup;
        const isProcessing = !doc.is_processed && doc.progress >= 0;
        const hasFailed = doc.is_processed && doc.progress === -1;
        const label = doc.original_filename || `Amendment ${doc.ordinal}`;

        return (
          <Accordion
            key={doc.id}
            expanded={expandedDoc === idx}
            onChange={(_, expanded) => setExpandedDoc(expanded ? idx : false)}
            sx={{
              mb: 1,
              boxShadow: 'none',
              border: '1px solid #E5E7EB',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: '0 0 8px 0' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 1,
                },
              }}
            >
              <DescriptionIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              <Typography sx={{ fontFamily: 'Roboto-Medium', fontSize: '14px', flex: 1 }}>
                {label}
              </Typography>
              {doc.effective_date && (
                <Chip
                  label={`Effective: ${doc.effective_date}`}
                  size='small'
                  sx={{ fontSize: '12px', height: '22px', mr: 1 }}
                />
              )}
              {isProcessing && (
                <Chip
                  label={`Processing ${doc.progress}%`}
                  size='small'
                  color='info'
                  sx={{ fontSize: '12px', height: '22px', mr: 1 }}
                />
              )}
              {hasFailed && (
                <Chip
                  label='Failed'
                  size='small'
                  color='error'
                  sx={{ fontSize: '12px', height: '22px', mr: 1 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isProcessing ? (
                <Box display='flex' alignItems='center' gap={1} p={2}>
                  <CircularProgress size={16} sx={{ color: '#00CFA1' }} />
                  <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                    {doc.message || 'Processing amendment...'}
                  </Typography>
                </Box>
              ) : hasFailed ? (
                <Box p={2}>
                  <Typography sx={{ fontSize: '13px', color: '#EF4444' }}>
                    {doc.message || 'Amendment processing failed.'}
                  </Typography>
                </Box>
              ) : abstractions.length === 0 ? (
                <Box p={2}>
                  <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                    No abstraction data available for this amendment.
                  </Typography>
                </Box>
              ) : (
                <Box className='lease-abstraction-tab' sx={{ height: 'auto !important', maxHeight: 'calc(100vh - 480px)' }}>
                  {/* Headings Sidebar */}
                  {headings.length > 0 && (
                    <Box className='headings-sidebar'>
                      <Box className='headings-inner'>
                        {headings.map((heading, hidx) => (
                          <Link
                            key={heading}
                            activeClass='active'
                            to={`${doc.id}-${heading}`}
                            smooth
                            duration={80}
                            offset={hidx === 0 ? -20 : -10}
                            isDynamic
                            spy
                            containerId={`amendment-scroll-${doc.id}`}
                            className='heading-link'
                          >
                            {heading}
                          </Link>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Content Area */}
                  <Box className='abstraction-content' id={`amendment-scroll-${doc.id}`}>
                    {abstractions.map((abs) => (
                      <Element name={`${doc.id}-${abs.title}`} key={abs.id} className='abstraction-section'>
                        <a
                          href={abs.pdf_url.startsWith('http') ? abs.pdf_url : `${process.env.REACT_APP_BASE_URL ?? ''}${abs.pdf_url}`}
                          target='_blank'
                          rel='noreferrer'
                          className='section-title'
                        >
                          {abs.title}
                        </a>
                        <div
                          className='section-content'
                          dangerouslySetInnerHTML={{ __html: normalizeHtmlContent(abs.description) }}
                        />
                      </Element>
                    ))}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
